import { Checkbox } from "@/components/checkbox";
import { Input } from "@/components/input";
import {
  usePlaybackMediator,
  useVideoPlayback,
} from "@/contexts/playback-context";
import { useViewSettings } from "./hooks";
import { useSelector } from "@legendapp/state/react";
import {
  LockOpen2Icon,
  LockClosedIcon,
  Cross2Icon,
  MixerVerticalIcon,
} from "@radix-ui/react-icons";
import {
  Popover,
  ScrollArea,
  Theme,
  IconButton,
  Button,
  Separator,
} from "@radix-ui/themes";

export function ViewSettings() {
  const viewSettings = useViewSettings();
  const videoPlayback = useVideoPlayback();
  const isVideoEnabled = useSelector(() => viewSettings.showVideo.get());
  const isStandalone = useSelector(() =>
    videoPlayback.videoContext.isStandalone.get()
  );
  const isCameraLabelEnabled = useSelector(() =>
    viewSettings.showCameraLabel.get()
  );
  const showCamera = useSelector(() => viewSettings.showCamera.get());
  const showStoreModel = useSelector(() => viewSettings.showStoreModel.get());
  const showTrackId = useSelector(() => viewSettings.showTrackId.get());
  const showTrackAvatar = useSelector(() => viewSettings.showTrackAvatar.get());
  const isSyncEnabled = useSelector(() => viewSettings.syncTabs.get());
  const isDebugInfoEnabled = useSelector(() =>
    viewSettings.showDebugInfo.get()
  );
  const isLastDetectionsMappingEnabled = useSelector(() =>
    viewSettings.showLastDetectionsPanel.get()
  );
  const showBins = useSelector(() => viewSettings.showBins.get());
  const showGondolas = useSelector(() => viewSettings.showGondolas.get());
  const showZones = useSelector(() => viewSettings.showZones.get());
  const showPlanogramLabels = useSelector(() =>
    viewSettings.showPlanogramLabels.get()
  );
  const showDetections = useSelector(() => viewSettings.showDetections.get());
  const showUnmatched = useSelector(() =>
    viewSettings.showUnmatchedDetections.get()
  );
  const showOverflowPoints = useSelector(() =>
    viewSettings.showOverflowPoints.get()
  );
  const showDetectionCamera = useSelector(() =>
    viewSettings.showDetectionCamera.get()
  );
  const showReprojections = useSelector(() =>
    viewSettings.showReprojections.get()
  );
  const showNewModel = useSelector(() => viewSettings.showNewModel.get());
  const showAbsoluteTime = useSelector(() =>
    viewSettings.showAbsoluteTime.get()
  );
  const isCameraLocked = useSelector(
    () => viewSettings.viewMode.get() === "detections"
  );
  const trackConfidence = useSelector(() => viewSettings.trackConfidence.get());
  const detectionConfidence = useSelector(() =>
    viewSettings.detectionConfidence.get()
  );
  const reprojectionConfidence = useSelector(() =>
    viewSettings.reprojectionConfidence.get()
  );
  const showSkuSectionView = useSelector(() =>
    viewSettings.showSkuSectionView.get()
  );
  const { toggleVideo, toggleStandaloneMode } = usePlaybackMediator();

  return (
    <div className="flex items-center">
      <div className="grow"></div>
      <Button
        highContrast
        variant="ghost"
        color="gray"
        size="1"
        mr="4"
        onClick={() => {
          if (isCameraLocked) {
            viewSettings.viewMode.set("snapshots");
          } else {
            viewSettings.viewMode.set("detections");
          }
        }}
      >
        {isCameraLocked ? <LockClosedIcon /> : <LockOpen2Icon />}
        Detections Mode
      </Button>

      <Popover.Root>
        <Popover.Trigger>
          <Button size="1" color="gray" variant="outline" className="p-1">
            <MixerVerticalIcon />
          </Button>
        </Popover.Trigger>
        <Popover.Content className="p-0">
          <Theme>
            <div className="flex items-center gap-2 p-2 leading-none">
              <MixerVerticalIcon className="m-1" />
              <span className="font-medium">Settings</span>
              <span className="grow"></span>
              <Popover.Close aria-label="Close">
                <IconButton variant="soft" color="gray">
                  <Cross2Icon width={16} height={16} />
                </IconButton>
              </Popover.Close>
            </div>
            <Separator size="4" />
            <div>
              <ScrollArea className="max-h-[300px]">
                <div className="pt-4 pl-4 pb-4 pr-4">
                  <div className="flex items-center gap-2 mb-6 leading-none">
                    <span className="font-medium">Scene settings</span>
                  </div>
                  <div className="flex flex-col gap-5 pl-2">
                    <Checkbox
                      label="Video"
                      checked={isVideoEnabled}
                      onCheckedChange={toggleVideo}
                    />
                    <Checkbox
                      label="SKU Section view"
                      checked={showSkuSectionView}
                      onCheckedChange={() => {
                        viewSettings.showSkuSectionView.toggle();
                      }}
                    />
                    <Checkbox
                      label="Standalone video"
                      checked={isStandalone}
                      onCheckedChange={toggleStandaloneMode}
                    />
                    <Checkbox label="Camera Interactive Mode" />
                    <Checkbox
                      label="Camera Label"
                      checked={isCameraLabelEnabled}
                      onCheckedChange={() => {
                        viewSettings.showCameraLabel.toggle();
                      }}
                    />
                    <Checkbox
                      label="Camera visibility"
                      checked={showCamera}
                      onCheckedChange={() => {
                        viewSettings.showCamera.toggle();
                      }}
                    />
                    <Checkbox
                      label="Store model visibility"
                      checked={showStoreModel}
                      onCheckedChange={() => {
                        viewSettings.showStoreModel.toggle();
                      }}
                    />

                    <Checkbox
                      label="Track ID"
                      checked={showTrackId}
                      onCheckedChange={() => {
                        viewSettings.showTrackId.toggle();
                      }}
                    />
                    <Checkbox
                      label="Track Image"
                      checked={showTrackAvatar}
                      onCheckedChange={() => {
                        viewSettings.showTrackAvatar.toggle();
                      }}
                    />
                    <Checkbox label="SKU Label" />
                    <Checkbox
                      label="Bins"
                      checked={showBins}
                      onCheckedChange={() => {
                        viewSettings.showBins.toggle();
                      }}
                    />
                    <Checkbox
                      label="Gondolas"
                      checked={showGondolas}
                      onCheckedChange={() => {
                        viewSettings.showGondolas.toggle();
                      }}
                    />
                    <Checkbox
                      label="Zones"
                      checked={showZones}
                      onCheckedChange={() => {
                        viewSettings.showZones.toggle();
                      }}
                    />
                    <Checkbox
                      label="Planogram Labels"
                      checked={showPlanogramLabels}
                      onCheckedChange={() => {
                        viewSettings.showPlanogramLabels.toggle();
                      }}
                    />
                    <Checkbox
                      label="Sync Tabs"
                      checked={isSyncEnabled}
                      onCheckedChange={() => {
                        viewSettings.syncTabs.toggle();
                      }}
                    />
                    <Checkbox
                      label="Photogrammetry model"
                      checked={showNewModel}
                      onCheckedChange={() => {
                        viewSettings.showNewModel.toggle();
                      }}
                    />
                    <Checkbox
                      label="Display absolute time"
                      checked={showAbsoluteTime}
                      onCheckedChange={() => {
                        viewSettings.showAbsoluteTime.toggle();
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-2 my-6 leading-none">
                    <span className="font-medium">Track debug settings</span>
                  </div>
                  <div className="flex flex-col gap-5 pl-2">
                    <Checkbox
                      label="Track snapshot (JSON)"
                      checked={isDebugInfoEnabled}
                      onCheckedChange={() => {
                        viewSettings.showDebugInfo.toggle();
                      }}
                    />
                    <Input
                      type="number"
                      value={trackConfidence}
                      onChange={(e) => {
                        viewSettings.trackConfidence.set(e.target.value as any);
                      }}
                      min={0}
                      max={1}
                      step={0.1}
                      label="Pose confidence filter"
                      className="h-10"
                    />
                    <Checkbox
                      label="Show last detections mapping"
                      checked={isLastDetectionsMappingEnabled}
                      onCheckedChange={() => {
                        viewSettings.showLastDetectionsPanel.toggle();
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-2 my-6 leading-none">
                    <span className="font-medium">
                      Camera View debug settings
                    </span>
                  </div>
                  <div className="flex flex-col gap-5 pl-2">
                    <Checkbox
                      label="Track 2D Reprojections"
                      checked={showReprojections}
                      onCheckedChange={() => {
                        viewSettings.showReprojections.toggle();
                      }}
                    />
                    <Checkbox
                      label="2D detections"
                      checked={showDetections}
                      onCheckedChange={() => {
                        viewSettings.showDetections.toggle();
                      }}
                    />
                    <Checkbox
                      label="2D unmatched"
                      checked={showUnmatched}
                      onCheckedChange={() => {
                        viewSettings.showUnmatchedDetections.toggle();
                      }}
                    />

                    <Checkbox
                      label="Show overflow detection points"
                      checked={showOverflowPoints}
                      onCheckedChange={() => {
                        viewSettings.showOverflowPoints.toggle();
                      }}
                    />

                    <Checkbox
                      label="Highlight last detection map"
                      checked={showDetectionCamera}
                      onCheckedChange={() => {
                        viewSettings.showDetectionCamera.toggle();
                      }}
                    />

                    <Input
                      id="detection-conf-filter"
                      type="number"
                      value={detectionConfidence}
                      onChange={(e) => {
                        viewSettings.detectionConfidence.set(
                          Number(e.target.value)
                        );
                      }}
                      min={0}
                      max={1}
                      step={0.1}
                      label="2D Detection confidence filter"
                      className="h-10"
                    />
                    <Input
                      id="reprojection-conf-filter"
                      type="number"
                      value={reprojectionConfidence}
                      onChange={(e) => {
                        viewSettings.reprojectionConfidence.set(
                          e.target.value as any
                        );
                      }}
                      min={0}
                      max={1}
                      step={0.1}
                      label="2D Reprojection confidence filter"
                      className="h-10"
                    />
                  </div>
                </div>
              </ScrollArea>
            </div>
            <Separator size="4" />
            <div className="p-2 flex justify-end">
              <Button variant="soft" color="gray" highContrast>
                Reset
              </Button>
            </div>
          </Theme>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
